<template>
  <authenticated-layout>
    <PageHeader title="Insights" description="Explore comprehensive analytics and generate detailed reports on user engagement and outcomes" />
    <v-container class="py-4" :fluid="true">

      <v-table-toolbar>
        <v-text-field
          v-model="searchQuery"
          label="Search Reports"
          variant="outlined"
          clearable
          density="compact"
          hide-details
          placeholder="Type to search reports by title..."
          class="bg-white pl-3"
          max-width="300px"
        ></v-text-field>
      </v-table-toolbar>
      <!-- Favorites Section -->
      <ReportGroupCard
        v-if="favoriteReports.length > 0"
        class="mb-6"
      >
        <template #icon>
          <i class="fas fa-star"></i>
        </template>
        <template #title>
          Favorites
        </template>
        <template #description>
          Your favorite reports for quick access
        </template>

        <v-col v-for="report in favoriteReports" :key="report.id" cols="4">
          <ReportListCard
            :routeName="report.routeName"
            :is-favorite="true"
            @toggle-favorite="toggleFavorite(report)"
          >
            <template #icon>
              <i :class="report.icon"></i>
            </template>
            <template #title>
              {{ report.title }}
            </template>
            <template #description>
              {{ report.description }}
            </template>
          </ReportListCard>
        </v-col>
      </ReportGroupCard>

      <ReportGroupCard
        v-for="(group, groupIndex) in filteredReportGroups"
        :key="groupIndex"
        :class="group.class"
      >
        <template #title>
          {{ group.title }}
        </template>
        <template #description>
          {{ group.description }}
        </template>

        <v-col v-for="(report, reportIndex) in group.reports" :key="reportIndex" cols="4">
          <ReportListCard
            :routeName="report.routeName"
            :is-favorite="isFavorite(report)"
            @toggle-favorite="toggleFavorite(report)"
          >
            <template #icon>
              <i :class="report.icon"></i>
            </template>
            <template #title>
              {{ report.title }}
            </template>
            <template #description>
              {{ report.description }}
            </template>
          </ReportListCard>
        </v-col>
      </ReportGroupCard>

    </v-container>
  </authenticated-layout>
</template>

<script setup lang="ts">
import AuthenticatedLayout from "@/layout/AuthenticatedLayout.vue";
import {useI18n} from "vue-i18n";
import ReportListCard from "@/pages/Insights/ReportsPage/ReportListCard.vue";
import ReportGroupCard from "@/pages/Insights/ReportsPage/ReportGroupCard.vue";
import {ref, computed, onMounted} from "vue";
import PageHeader from "@/components/PageHeader.vue";

interface Report {
  routeName: string;
  icon: string;
  title: string;
  description: string;
  id?: string;
}

const {t} = useI18n();

const searchQuery = ref("");
const favorites = ref<string[]>([]);

const reportGroups = [
  {
    title: "Dashboards",
    description: "Visualize key metrics and insights at a glance",
    reports: [
      {
        routeName: "group-dashboard",
        icon: "far fa-ticket",
        title: "Group Analysis",
        description: "Analyze group performance and engagement metrics",
        id: "group-dashboard",
      },
    ],
  },
  {
    class: "mt-6",
    title: "Usage Analytics",
    description: "Track user engagement and platform utilization",
    reports: [
      {
        routeName: "voucher-usage",
        icon: "far fa-receipt",
        title: "License Usage",
        description: "Analyze license redemption patterns and trends",
        id: "voucher-usage",
      },
      {
        routeName: "subject-choice-completion",
        icon: "far fa-check-circle",
        title: "Subject Choice Completion",
        description: "Analyze subject choice completion patterns",
        id: "subject-choice-completion",
      }
    ],
  },
  {
    class: "mt-6",
    title: "Assessment Completion",
    description: "Track completion rates for various assessments",
    reports: [
      {
        routeName: "worker-type-completion",
        icon: "far fa-clipboard-check",
        title: "Worker Type Completion",
        description: "Track completion rates for worker type assessments",
        id: "worker-type-completion",
      },
      {
        routeName: "personality-type-completion",
        icon: "far fa-user-tag",
        title: "Personality Type Completion",
        description: "Track completion rates for personality traits assessments",
        id: "personality-type-completion",
      },
      {
        routeName: "value-scales-completion",
        icon: "far fa-balance-scale",
        title: "Value Scales Completion",
        description: "Track completion rates for Value Scales assessments",
        id: "value-scales-completion",
      },
      {
        routeName: "learning-styles-completion",
        icon: "far fa-book-open",
        title: "Learning Styles Completion",
        description: "Track completion rates for Learning Styles assessments",
        id: "learning-styles-completion",
      },
      {
        routeName: "rock-paper-scissors-completion",
        icon: "far fa-hand-rock",
        title: "Rock Paper Scissors",
        description: "Analyze results of Rock Paper Scissors games",
        id: "rock-paper-scissors-completion",
      },
      {
        routeName: "maze-completion",
        icon: "far fa-puzzle-piece",
        title: "Maze Assessment Completion",
        description: "Track completion rates for Maze Assessments",
        id: "maze-completion",
      },
      {
        routeName: "perceiving-emotions-completion",
        icon: "far fa-smile",
        title: "Perceiving Emotions",
        description: "Track completion rates for Perceiving Emotion Assessments",
        id: "perceiving-emotions-completion",
      },
      {
        routeName: "managing-emotions-completion",
        icon: "far fa-smile",
        title: "Managing Emotions",
        description: "Track completion rates for Managing Emotion Assessments",
        id: "managing-emotions-completion",
      },
      {
        routeName: "understanding-emotions-completion",
        icon: "far fa-smile",
        title: "Understanding Emotions",
        description: "Track completion rates for Understanding Emotion Assessments",
        id: "understanding-emotions-completion",
      },
    ]
  },

  {
    class: "mt-6",
    title: "User Results",
    description: "Explore individual user results and choices",
    reports: [
      {
        routeName: "user-subject-choices",
        icon: "far fa-user",
        title: t("components.ReportPage.reports.users.user_subject_choices"),
        description: "View detailed reports on user subject choices",
        id: "user-subject-choices",
      },
      {
        routeName: "user-worker-type-results",
        icon: "far fa-users-cog",
        title: t("components.ReportPage.reports.users.user_worker_types"),
        description: "Explore user worker type assessments",
        id: "user-worker-type-results",
      },
      {
        routeName: "user-personality-trait-results",
        icon: "far fa-id-badge",
        title: t("components.ReportPage.reports.users.user_personality_types"),
        description: "Analyze personality type results of users",
        id: "user-personality-types",
      },
      {
        routeName: "user-value-scales-results",
        icon: "far fa-balance-scale",
        title: "Value Scales Results",
        description: "View user results for Value Scales assessments",
        id: "user-value-scales-results",
      },
      {
        routeName: "user-learning-styles-results",
        icon: "far fa-book-reader",
        title: "Learning Styles Results",
        description: "Explore user learning styles assessments",
        id: "user-learning-styles-results",
      },
    ],
  },
  {
    class: "mt-6",
    title: "Distribution",
    description: "Explore individual user results and choices",
    reports: [
      {
        routeName: "subject-choice-distribution",
        icon: "far fa-chart-pie",
        title: t("components.ReportPage.reports.distributions.subject_choice_distribution"),
        description: "Analyze the distribution of subject choices among users",
        id: "subject-choice-distribution",
      },
      {
        routeName: "worker-type-distribution",
        icon: "far fa-users",
        title: t("components.ReportPage.reports.distributions.worker_type_distribution"),
        description: "Explore the distribution of worker types across the platform",
        id: "worker-type-distribution",
      },
      {
        routeName: "personality-traits-distribution",
        icon: "far fa-user-circle",
        title: t("components.ReportPage.reports.distributions.personality_type_distribution"),
        description: "Examine the distribution of personality traits among users",
        id: "personality-traits-distribution",
      },
    ],
  },
];

onMounted(() => {
  const storedFavorites = localStorage.getItem("reportFavorites");
  if (storedFavorites) {
    favorites.value = JSON.parse(storedFavorites);
  }
});

// Get all favorite reports for the favorites section
const favoriteReports = computed(() => {
  const allReports: Report[] = [];
  reportGroups.forEach((group) => {
    group.reports.forEach((report) => {
      if (report.id && favorites.value.includes(report.id)) {
        allReports.push(report);
      }
    });
  });
  return allReports;
});

// Check if a report is in favorites
function isFavorite(report: Report): boolean {
  return report.id ? favorites.value.includes(report.id) : false;
}

// Toggle favorite status for a report
function toggleFavorite(report: Report): void {
  if (!report.id) return;

  const index = favorites.value.indexOf(report.id);
  if (index === -1) {
    // Add to favorites
    favorites.value.push(report.id);
  } else {
    // Remove from favorites
    favorites.value.splice(index, 1);
  }

  // Save to localStorage
  localStorage.setItem("reportFavorites", JSON.stringify(favorites.value));
}

const filteredReportGroups = computed(() => {
  if (!searchQuery.value) return reportGroups;
  return reportGroups.map((group) => {
    const filteredReports = group.reports.filter((report) =>
      report.title.toLowerCase().includes(searchQuery.value.toLowerCase()));
    return {
      ...group,
      reports: filteredReports,
    };
  }).filter((group) => group.reports.length > 0);
});
</script>
